import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Target, Gift } from 'lucide-react';
import { useMyPrograms, useAvailablePrograms } from '@/lib/queries/programs';
import { EmptyState } from '@/components/shared/empty-state';
import { ProgressBar } from '@/components/shared/progress-bar';
import { Badge } from '@/components/shared/badge';
import { Skeleton } from '@/components/shared/skeleton';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/app/programs/')({
  component: ProgramsPage,
});

type Tab = 'my' | 'available';

function ProgramsPage() {
  const [tab, setTab] = useState<Tab>('my');

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-bold text-foreground">Programs</h1>

      <div className="flex gap-1 rounded-lg bg-muted p-1 w-fit">
        {(['my', 'available'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              'px-4 py-1.5 text-sm font-medium rounded-md transition-colors',
              tab === t
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {t === 'my' ? 'My Programs' : 'Available'}
          </button>
        ))}
      </div>

      {tab === 'my' ? <MyProgramsTab /> : <AvailableTab />}
    </div>
  );
}

function MyProgramsTab() {
  const { data, isLoading } = useMyPrograms();

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-5 space-y-3">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-2 w-full" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>
    );
  }

  const programs = data?.programs;
  if (!programs?.length) {
    return (
      <EmptyState
        icon={Target}
        title="No active programs"
        description="You haven't enrolled in any programs yet. Check the Available tab to find programs."
      />
    );
  }

  return (
    <div className="space-y-3">
      {programs.map(
        (p: {
          program_id: string;
          program_name: string;
          program_type?: string;
          progress_percent?: number;
          benefits_count?: number;
          days_remaining?: number;
        }) => (
          <Link
            key={p.program_id}
            to="/app/programs/$programId"
            params={{ programId: p.program_id }}
            className="block rounded-xl border border-border bg-card p-5 hover:bg-accent/30 transition-colors"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-foreground">{p.program_name}</p>
                {p.program_type && <Badge variant="blue">{p.program_type}</Badge>}
              </div>
              {p.progress_percent != null && (
                <span className="text-sm font-bold text-primary tabular-nums">
                  {Math.round(p.progress_percent)}%
                </span>
              )}
            </div>
            {p.progress_percent != null && (
              <ProgressBar value={p.progress_percent} className="mb-2" />
            )}
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              {p.benefits_count != null && (
                <span className="flex items-center gap-1">
                  <Gift className="h-3.5 w-3.5" />
                  {p.benefits_count} benefit{p.benefits_count !== 1 ? 's' : ''}
                </span>
              )}
              {p.days_remaining != null && <span>{p.days_remaining} days left</span>}
            </div>
          </Link>
        )
      )}
    </div>
  );
}

function AvailableTab() {
  const { data, isLoading } = useAvailablePrograms();

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-4">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-3 w-32 mt-2" />
          </div>
        ))}
      </div>
    );
  }

  const programs = data?.programs;
  if (!programs?.length) {
    return (
      <EmptyState
        icon={Target}
        title="No programs available"
        description="No programs are currently available for your company."
      />
    );
  }

  return (
    <div className="space-y-2">
      {programs.map(
        (p: {
          program_id: string;
          program_name: string;
          program_type?: string;
          enrollment_mode?: string;
          benefits_count?: number;
        }) => (
          <Link
            key={p.program_id}
            to="/app/programs/$programId"
            params={{ programId: p.program_id }}
            className="flex items-center justify-between rounded-xl border border-border bg-card p-4 hover:bg-accent/30 transition-colors"
          >
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-foreground">{p.program_name}</p>
                {p.program_type && <Badge variant="blue">{p.program_type}</Badge>}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {p.benefits_count ?? 0} benefits &middot; {p.enrollment_mode ?? 'auto'}
              </p>
            </div>
            <span className="text-xs font-medium text-primary">View &rarr;</span>
          </Link>
        )
      )}
    </div>
  );
}
